import React, { ReactNode } from 'react';

import { DialogContent, DialogOverlay } from '@reach/dialog';
import { defineMessages, useIntl } from 'react-intl';
import styled, { ThemeProvider } from 'styled-components';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IOffer } from '@rbi-ctg/menu';
import CloseIcon from 'components/icons/close';
import { theme } from 'components/layout/brand-theme';
import { OfferDisclaimer } from 'components/offers/ui-refresh/offer-disclaimer';
import useEffectOnce from 'hooks/use-effect-once';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background-color: ${Styles.color.grey.five};
  border: none;
  border-radius: 9999px;
  padding: 0;
  height: 3rem;
  width: 3rem;

  :hover {
    cursor: pointer;
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    height: 2.25rem;
    transform: translate(-50%, -50%);
  }
`;

const ModalContainer = styled(DialogOverlay)`
  background: rgba(0, 0, 0, 0.7);
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  /* TODO: refactor z-indexes */
  z-index: ${Styles.zIndex.max - 200};
  display: flex;
  justify-content: center;
  align-content: center;
  ${Styles.mobile`
    padding: env(safe-area-inset-top) env(safe-area-inset-left) env(safe-area-inset-bottom) env(safe-area-inset-right);
  `}
  [data-reach-dialog-content] {
    background: ${Styles.color.grey.five};
    border-radius: ${Styles.borderRadius};
    position: relative;
    margin: auto;
  }
`;

const InnerContent = styled.div`
  padding: 1.5rem 1rem;
  max-width: 90vw;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${Styles.desktop`
    flex-direction: row;
  `}
`;

const Heading = styled.h2`
  font-family: ${Styles.fontFamily.base};
  text-transform: ${Styles.textTransform.headlines};
  text-align: center;
  line-height: 1.15;
  max-width: 30rem;
  margin: 0 auto;
`;

const Body = styled.p`
  font-family: ${Styles.fontFamily.body};
  text-align: center;

  ${Styles.desktop`
    max-width: 23rem;
    margin: 0 auto;
    padding: 0.5rem;
  `}
`;

const Disclaimer = styled.div`
  text-align: center;
  color: ${Styles.color.grey.one};
  font-style: normal;
  font-size: 0.75rem;
  max-width: ${Styles.layout.smallerSectionMaxWidth};
  margin: 2rem auto 0;
  font-family: ${Styles.fontFamily.body};
`;

export interface IDialogProps extends IBaseProps {
  heading: ReactNode | string;
  body?: string;
  bodyComponent?: ReactNode;
  onDismiss: () => void;
  actions?: ReactNode;
  image?: ReactNode;
  disclaimer?: ReactNode;
  showCloseButton?: boolean;
  modalAppearanceEventMessage: string;
  errorEventMessage?: string;
  offer?: IOffer | null;
}

const Dialog = ({
  className,
  heading,
  body,
  bodyComponent,
  onDismiss,
  actions,
  disclaimer,
  image,
  offer,
  /**
   * A small string sent to mParticle describing the purpose of the modal.
   */
  modalAppearanceEventMessage,
  /**
   * Additional Error details to be sent to mParticle.
   */
  errorEventMessage,
  'data-testid': dataTestId,
  showCloseButton = false
}: IDialogProps) => {
  const { logEvent } = useMParticleContext();

  useEffectOnce(() => {
    logEvent(CustomEventNames.MODAL_APPEARANCE, EventTypes.Other, {
      Message: modalAppearanceEventMessage,
      ErrorMessage: errorEventMessage,
      ModalHeader: typeof heading === 'string' ? heading : '',
      ModalMessage: body || ''
    });
  });

  const { formatMessage } = useIntl();

  return (
    <ThemeProvider theme={theme}>
      <ModalContainer isOpen onDismiss={onDismiss}>
        <DialogContent aria-labelledby="modal-heading-text" className={className}>
          {image}
          <InnerContent data-testid={dataTestId}>
            {showCloseButton && (
              <CloseButton aria-label={formatMessage(messages.closeDialog)} onClick={onDismiss}>
                <CloseIcon />
              </CloseButton>
            )}
            <Heading data-testid="coupon-redemption-dialog" id="modal-heading-text">
              {heading}
            </Heading>
            {bodyComponent || <Body aria-live="polite">{body}</Body>}
            {actions && <Actions>{actions}</Actions>}
            {offer && <OfferDisclaimer offer={offer} />}
            {disclaimer && <Disclaimer>{disclaimer}</Disclaimer>}
          </InnerContent>
        </DialogContent>
      </ModalContainer>
    </ThemeProvider>
  );
};

export default Dialog;

const messages = defineMessages({
  closeDialog: {
    id: 'closeDialog',
    defaultMessage: 'Close Dialog',
    description: 'Button to close the dialog box'
  }
});
